import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const CategoryRow = ({ category, depth, onDelete, canCreate, canEdit, canDelete }) => {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = category.children && category.children.length > 0;

    return (
        <>
            <div
                className="flex items-center justify-between gap-3 py-3 px-4 border-b hover:bg-gray-50"
                style={{ paddingLeft: `${16 + depth * 28}px` }}
            >
                <div className="flex items-center gap-2 min-w-0">
                    {hasChildren ? (
                        <button onClick={() => setExpanded(!expanded)} className="text-gray-500 flex-shrink-0">
                            {expanded ? <ExpandLessIcon sx={{ fontSize: "20px" }} /> : <ExpandMoreIcon sx={{ fontSize: "20px" }} />}
                        </button>
                    ) : (
                        <span className="w-5 flex-shrink-0" />
                    )}
                    <span className="font-medium text-gray-800 truncate">{category.name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">/{category.slug}</span>
                    <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 flex-shrink-0">
                        {category.productCount} product{category.productCount !== 1 ? "s" : ""}
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {canCreate && (
                        <Link to={`/admin/category/new?parent=${category._id}`} className="text-gray-400 hover:text-primary-blue" title="Add subcategory">
                            <AddIcon sx={{ fontSize: "20px" }} />
                        </Link>
                    )}
                    {canEdit && (
                        <Link to={`/admin/category/${category._id}`} className="text-gray-400 hover:text-primary-blue" title="Edit">
                            <EditIcon sx={{ fontSize: "18px" }} />
                        </Link>
                    )}
                    {canDelete && (
                        <button onClick={() => onDelete(category)} className="text-gray-400 hover:text-red-500" title="Delete">
                            <DeleteOutlineIcon sx={{ fontSize: "20px" }} />
                        </button>
                    )}
                </div>
            </div>
            {hasChildren && expanded && category.children.map((child) => (
                <CategoryRow key={child._id} category={child} depth={depth + 1} onDelete={onDelete} canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />
            ))}
        </>
    );
};

const CategoryTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.categories || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchCategories = async () => {
        try {
            const { data } = await axios.get('/api/v1/categories');
            setCategories(data.categories);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load categories", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (category) => {
        if (!window.confirm(`Delete "${category.name}"? This can't be undone.`)) return;
        try {
            await axios.delete(`/api/v1/admin/category/${category._id}`);
            enqueueSnackbar("Category deleted", { variant: "success" });
            fetchCategories();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete category", { variant: "error" });
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Categories | 1313 Mart" />

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Categories</h1>
                {canCreate && (
                    <Link to="/admin/category/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Category</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full overflow-hidden">
                {categories.length === 0 ? (
                    <p className="text-center text-gray-400 py-10">No categories yet.</p>
                ) : (
                    categories.map((cat) => (
                        <CategoryRow key={cat._id} category={cat} depth={0} onDelete={handleDelete} canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />
                    ))
                )}
            </div>
        </>
    );
};

export default CategoryTable;